/**
 * reCAPTCHA Verify Token Management Module
 * Manages backend-verified tokens (not raw Google tokens)
 */

import { encode as base64Encode } from 'js-base64';

import { getRecaptchaToken } from './recaptcha';

import { calculateSignature } from '~/security/signature';

// reCAPTCHA verify token cookie name and max age
const RECAPTCHA_VERIFY_TOKEN_COOKIE_NAME = '_recaptcha_verify_token_';
const RECAPTCHA_VERIFY_TOKEN_MAX_AGE = 60 * 60 * 24 * 30; // 30 days

// Whether reCAPTCHA verification is enabled (configured via VITE_ENABLE_RECAPTCHA env var)
const FLAG_ENABLE_RECAPTCHA = import.meta.env.VITE_ENABLE_RECAPTCHA === 'true';

/**
 * Get reCAPTCHA verify token from cookie
 */
export function getRecaptchaVerifyTokenFromCookie(): string | null {
  if (typeof document === 'undefined') return null;

  const cookie = document.cookie.split('; ').find((row) => row.startsWith(`${RECAPTCHA_VERIFY_TOKEN_COOKIE_NAME}=`));

  return cookie ? cookie.split('=')[1] : null;
}

/**
 * Save reCAPTCHA verify token to cookie
 */
export function saveRecaptchaVerifyTokenToCookie(token: string) {
  if (typeof document === 'undefined') return;

  document.cookie = `${RECAPTCHA_VERIFY_TOKEN_COOKIE_NAME}=${token}; path=/; max-age=${RECAPTCHA_VERIFY_TOKEN_MAX_AGE}; SameSite=Lax`;
  console.log('[RecaptchaVerify] Token saved to cookie');
}

/**
 * Clear reCAPTCHA verify token from cookie
 */
export function clearRecaptchaVerifyTokenFromCookie() {
  if (typeof document === 'undefined') return;

  document.cookie = `${RECAPTCHA_VERIFY_TOKEN_COOKIE_NAME}=; path=/; max-age=0`;
  console.log('[RecaptchaVerify] Token cleared from cookie');
}

/**
 * Verify reCAPTCHA token and get score
 * @param recaptchaToken Token returned by Google reCAPTCHA
 * @param apiOrigin API origin
 * @param headers Base request header info
 * @returns Verified token (for subsequent requests)
 */
export async function verifyRecaptchaToken(
  recaptchaToken: string,
  apiOrigin: string,
  headers: {
    deviceId: string;
    channelType: string;
    sessionId: string;
    lang: string;
  }
): Promise<string> {
  console.log('[RecaptchaVerify] Verifying recaptcha token...');

  try {
    const verifyUrl = apiOrigin + '/api/user/verification/recaptcha-token';
    const timestamp = Date.now();

    // Build verification request headers
    const verifyHeaders = new Headers();
    verifyHeaders.set('X-Device-Id', headers.deviceId);
    verifyHeaders.set('X-Channel-Type', headers.channelType);
    verifyHeaders.set('X-Front-Session-Id', headers.sessionId);
    verifyHeaders.set('accept-language', headers.lang);
    verifyHeaders.set('content-type', 'application/json');
    verifyHeaders.set('X-Client-Type', import.meta.env.VITE_APP_CLIENT);
    verifyHeaders.set('accept', 'application/json');
    verifyHeaders.set('X-Timestamp', String(timestamp));

    // Add signature
    const signature = document.cookie
      .split('; ')
      .find((row) => row.startsWith('_signature_='))
      ?.split('=')[1];

    if (signature) {
      verifyHeaders.set('X-Server-Seed', base64Encode(signature));

      const calculatedSignature = calculateSignature(
        signature,
        timestamp,
        'POST',
        '/api/user/verification/recaptcha-token',
        headers.deviceId
      );
      verifyHeaders.set('X-Signature', calculatedSignature);
    }

    const response = await fetch(verifyUrl, {
      method: 'POST',
      headers: verifyHeaders,
      body: JSON.stringify({ 'g-recaptcha-response': recaptchaToken }),
    });

    if (!response.ok) {
      throw new Error(`Verification failed: ${response.status}`);
    }

    const result = await response.json();

    if (!result.success) {
      throw new Error(result.errMessage || 'Verification failed');
    }

    // Get verified token from response
    const verifyToken = result.data || '';

    if (!verifyToken) {
      throw new Error('No verify token in response');
    }

    console.log('[RecaptchaVerify] Verified successfully, score:', result.data?.score);

    // Save to cookie
    saveRecaptchaVerifyTokenToCookie(verifyToken);

    return verifyToken;
  } catch (error) {
    console.error('[RecaptchaVerify] Verification error:', error);
    throw error;
  }
}

/**
 * Ensure a valid verify token is obtained
 * Uses existing token from cookie if available, otherwise triggers the full verification flow
 * @param apiOrigin API origin
 * @param headers Base request header info
 * @returns Verify token (may be null if verification fails)
 */
export async function ensureRecaptchaVerifyToken(
  apiOrigin: string,
  headers: {
    deviceId: string;
    channelType: string;
    sessionId: string;
    lang: string;
  }
): Promise<string | null> {
  // Check if verify token already exists
  let verifyToken: any = '';

  if (!FLAG_ENABLE_RECAPTCHA) return '';

  if (FLAG_ENABLE_RECAPTCHA) {
    verifyToken = getRecaptchaVerifyTokenFromCookie();
  }

  if (!verifyToken) {
    console.log('[RecaptchaVerify] No verify token in cookie, starting verification...');

    try {
      // 1. Get Google reCAPTCHA token
      const recaptchaToken = await getRecaptchaToken();
      console.log('[RecaptchaVerify] Got recaptcha token:', recaptchaToken.substring(0, 20) + '...');

      // 2. Call backend verification API to get score and verify token
      verifyToken = await verifyRecaptchaToken(recaptchaToken, apiOrigin, headers);
      console.log('[RecaptchaVerify] Got verify token:', verifyToken.substring(0, 20) + '...');
    } catch (error) {
      console.error('[RecaptchaVerify] Verification process failed:', error);
      // Return null on verification failure, let the caller decide how to handle
      return null;
    }
  } else {
    console.log('[RecaptchaVerify] Using existing verify token from cookie');
  }

  return verifyToken;
}
